import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * The `?project=<id>` navigation convention, in one place — see ADR 0038. `useSelectedProject` /
 * `useSelectedProjects` (ADR 0042) build on it to also carry the selection across plain nav-link
 * clicks, not just explicit deep links.
 *
 * Any link that means "this project, on that page" carries the id in the query string, and the
 * landing page opens with it already selected. Every project-scoped page (Project Schedule, Project
 * Calendar, Project Registers, Project Launch, BPA Tracker) reads it through the hooks below rather
 * than growing its own copy, because the copy is where this went wrong: Schedule was the only page
 * that read the parameter at all, and even there the selection was being wiped before it could be used.
 */

export const PROJECT_PARAM = 'project';

/**
 * Build a link that carries a project. `extraParams` first so the project id reads last —
 * `/registers?tab=raaidd&project=<id>`.
 *
 * The API builds the same shape server-side for Project Calendar's event links (see
 * `calendar.service.ts`); this is the browser-side half of the one convention, not a second one.
 */
export function projectPath(path, projectId, extraParams) {
  // `path` may already carry its own query string (a checklist item's linkTarget like
  // `/registers?tab=raaidd`, say) -- split it out and merge rather than appending a second `?`,
  // which produced a URL neither `useSearchParams` nor the browser parsed as intended (found
  // 2026-09-07: every Project Launch checklist link into Registers/Schedule landed on a blank page).
  const [base, existingQuery] = path.split('?');
  const params = new URLSearchParams(existingQuery || '');
  for (const [key, value] of Object.entries(extraParams || {})) params.set(key, value);
  if (projectId) params.set(PROJECT_PARAM, projectId);
  const query = params.toString();
  return query ? `${base}?${query}` : base;
}

/**
 * The project id this page was OPENED with, stable for the page's life.
 *
 * Read exactly once, deliberately: pages that write their own query string afterwards (Registers
 * puts its active tab there) must not re-apply the deep link and override whatever the user has
 * picked since.
 */
export function useProjectDeepLink() {
  const { search } = useLocation();
  const [projectId] = useState(() => new URLSearchParams(search).get(PROJECT_PARAM) || '');
  return projectId;
}

const LAST_PROJECT_KEY = 'psOpsHub.lastProjectId';
const LAST_PROJECTS_KEY = 'psOpsHub.lastProjectIds';

// localStorage throws in private-browsing/storage-disabled contexts — persistence just degrades to
// per-visit (session state still works) rather than breaking the page.
function readStorage(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw === null ? fallback : raw;
  } catch {
    return fallback;
  }
}
function writeStorage(key, value) {
  try {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } catch { /* see readStorage */ }
}

/**
 * The selected project, carried across every page on ADR 0038's convention until the user picks a
 * different one — an explicit `?project=<id>` link always wins on arrival; otherwise this restores
 * whichever project was last selected on any of them. Persisted per browser (localStorage), not
 * per-user server-side — see ADR 0042.
 *
 * `excludeValue` (a stable constant, not a fresh literal each render) skips persisting one sentinel
 * that isn't a real project id — BPA Tracker's library mode. Without it, picking the library would
 * overwrite every other page's remembered project with a value none of them can resolve.
 */
export function useSelectedProject(excludeValue) {
  const deepLinkedProjectId = useProjectDeepLink();
  const [selected, setSelectedState] = useState(
    () => deepLinkedProjectId || readStorage(LAST_PROJECT_KEY, ''),
  );
  const setSelected = useCallback((next) => {
    setSelectedState((prev) => {
      const value = typeof next === 'function' ? next(prev) : next;
      if (value !== excludeValue) writeStorage(LAST_PROJECT_KEY, value);
      return value;
    });
  }, [excludeValue]);
  return [selected, setSelected];
}

/**
 * The multi-select form, for Project Calendar — same idea, storing a JSON array. It also shares the
 * single-project key, or the project picked on every other page was never carried here (and vice
 * versa): a lone selection is written to it, and on arrival the single project wins unless the saved
 * list already includes it.
 */
export function useSelectedProjects() {
  const deepLinkedProjectId = useProjectDeepLink();
  const [selected, setSelectedState] = useState(() => {
    if (deepLinkedProjectId) return [deepLinkedProjectId];
    let saved = [];
    try {
      saved = JSON.parse(readStorage(LAST_PROJECTS_KEY, '[]'));
    } catch { /* fall through to the single project */ }
    const last = readStorage(LAST_PROJECT_KEY, '');
    return last && !saved.includes(last) ? [last] : saved;
  });
  const setSelected = useCallback((next) => {
    setSelectedState((prev) => {
      const value = typeof next === 'function' ? next(prev) : next;
      writeStorage(LAST_PROJECTS_KEY, value.length ? JSON.stringify(value) : '');
      // Emptying the list clears the shared key too, or the next visit would restore it from there.
      if (value.length <= 1) writeStorage(LAST_PROJECT_KEY, value[0] || '');
      return value;
    });
  }, []);
  return [selected, setSelected];
}

/**
 * Which of `selected` the picker can still honor.
 *
 * **`ready` is the whole point.** `visibleProjects` is `[]` on the first render of every one of
 * these pages, so a bare "drop what the list doesn't offer" check fires once with nothing to match
 * against and clears a deep link a few milliseconds before `/projects` answers. That was the bug
 * behind the Portfolio Timeline's "See project schedule" link never landing on a project, from the
 * day it shipped (found 2026-09-05, by the Project Calendar's event links doing the same thing).
 *
 * `keep` exempts a sentinel that is legitimately not a project row — BPA Tracker's library mode.
 */
export function keptProjectId(selected, visibleProjects, ready, keep) {
  if (!selected) return '';
  if (!ready) return selected;
  if (keep && selected === keep) return selected;
  return visibleProjects.some((p) => p.id === selected) ? selected : '';
}

/** The multi-select form, for Project Calendar. Returns `selected` itself when nothing is dropped. */
export function keptProjectIds(selected, visibleProjects, ready) {
  if (!ready || !selected.length) return selected;
  const visible = new Set(visibleProjects.map((p) => p.id));
  const next = selected.filter((id) => visible.has(id));
  return next.length === selected.length ? selected : next;
}

/**
 * Clears a selected project the picker no longer offers — after unticking "show archived" with an
 * archived project selected, say. Dropping back to the placeholder rather than to another project
 * is deliberate: silently swapping in someone else's data is worse than asking again.
 */
export function usePruneMissingProject(selected, setSelected, visibleProjects, ready, keep) {
  useEffect(() => {
    const kept = keptProjectId(selected, visibleProjects, ready, keep);
    if (kept !== selected) setSelected(kept);
  }, [selected, setSelected, visibleProjects, ready, keep]);
}

export function usePruneMissingProjects(selected, setSelected, visibleProjects, ready) {
  useEffect(() => {
    const kept = keptProjectIds(selected, visibleProjects, ready);
    if (kept !== selected) setSelected(kept);
  }, [selected, setSelected, visibleProjects, ready]);
}
